// @story HIR-HR27
// A school's own written paper on a written-test stage (server:
// services/writtenTest.service.js, routes/writtenTest.routes.js):
//   • the answer key — nobody is marked until a named person confirms it
//     against the paper's own Google Form; held answers are marked then;
//   • questions whose key awards nothing until fixed (ambiguous, missing,
//     unreadable), each fixed here with a reason;
//   • in the candidate table: which paper a candidate sits (a stage with
//     several papers) and a person's mark for the written answer.
// Every change is shown first (the server's preview) and made only on
// Confirm. Confirming or fixing a key is the paper approver's; the server
// checks it. Nothing here messages a candidate.
import React, { useCallback, useEffect, useState } from 'react';
import api from '../../services/api';
import { Button, Card, CardHeader, StatusPill } from '../../ds';

const REASON_MIN = 12;
const small = { fontSize: 'var(--fs-caption)', color: 'var(--text-muted)' };
const field = { width: '100%', border: '1px solid var(--border, #ddd)', borderRadius: 6, padding: '4px 6px', font: 'inherit', fontSize: 'var(--fs-body-sm)' };
const errText = (e, fallback) => e?.response?.data?.message || fallback;

/* The stage's papers, for the panel and the candidate table. */
export function useWrittenPapers(jobId, stageId, enabled) {
  const [data, setData] = useState(null);
  const load = useCallback(async () => {
    if (!enabled) return;
    try {
      const res = await api.get(`/written-test/${jobId}/${stageId}/papers`);
      setData(res.data?.data || null);
    } catch (e) {
      setData(null);
    }
  }, [jobId, stageId, enabled]);
  useEffect(() => { load(); }, [load]);
  return { papers: data?.papers || [], choiceNeeded: Boolean(data?.choiceNeeded), reload: load };
}

/* Preview first, then Confirm: the server says what would change. */
function usePreviewThenConfirm(url, onDone) {
  const [shown, setShown] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const run = async (body, preview) => {
    setBusy(true); setErr('');
    try {
      const res = await api.post(url, { ...body, preview });
      if (preview) setShown(res.data?.data || {});
      else { setShown(null); if (onDone) onDone(res.data?.data); }
    } catch (e) {
      setErr(errText(e, 'Could not do that just now. Nothing was changed.'));
    } finally { setBusy(false); }
  };
  return { shown, busy, err, setErr, preview: (b) => run(b, true), confirm: (b) => run(b, false), cancel: () => setShown(null) };
}

function ConfirmKey({ paper, onChanged }) {
  const [formUrl, setFormUrl] = useState('');
  const [note, setNote] = useState('');
  const flow = usePreviewThenConfirm(`/written-test/papers/${paper.materialId}/confirm-key`, onChanged);
  const body = { formUrl: formUrl.trim(), note: note.trim() };
  return (
    <div style={{ display: 'grid', gap: 6, maxWidth: 560 }}>
      <label style={small} htmlFor={`form-${paper.materialId}`}>The paper's own Google Form you checked the key against</label>
      <input id={`form-${paper.materialId}`} style={field} value={formUrl} placeholder="https://docs.google.com/forms/…" onChange={(e) => { setFormUrl(e.target.value); flow.cancel(); }} />
      <input aria-label="Note" style={field} value={note} placeholder="Note (optional)" onChange={(e) => setNote(e.target.value)} />
      {!flow.shown ? (
        <div><Button size="sm" disabled={flow.busy || !body.formUrl} onClick={() => flow.preview(body)}>Check before confirming</Button></div>
      ) : (
        <div style={{ display: 'grid', gap: 6, padding: 10, borderRadius: 8, background: 'var(--surface-2, #f6f6f6)' }}>
          <span>
            Confirm this key as checked against <strong>{flow.shown.formUrl}</strong>, under your name.{' '}
            {flow.shown.toMark ? `${flow.shown.toMark} held candidate${flow.shown.toMark === 1 ? ' is' : 's are'} marked at once.` : 'Nobody is waiting to be marked yet.'}
          </span>
          <div style={{ display: 'flex', gap: 6 }}>
            <Button size="sm" disabled={flow.busy} onClick={() => flow.confirm(body)}>Confirm the key</Button>
            <Button size="sm" variant="ghost" disabled={flow.busy} onClick={flow.cancel}>Cancel</Button>
          </div>
        </div>
      )}
      {flow.err && <span role="alert" style={{ ...small, color: 'var(--status-danger-text)' }}>{flow.err}</span>}
    </div>
  );
}

function FixKey({ paper, issue, onChanged }) {
  const [answer, setAnswer] = useState('');
  const [reason, setReason] = useState('');
  const flow = usePreviewThenConfirm(`/written-test/papers/${paper.materialId}/questions/${issue.questionId}/key`, onChanged);
  const body = { correctAnswer: answer.trim(), reason: reason.trim() };
  const ready = body.correctAnswer && body.reason.length >= REASON_MIN;
  return (
    <li style={{ display: 'grid', gap: 4, padding: '8px 0', borderTop: '1px solid var(--border, #eee)' }}>
      <span><strong>Q{issue.number}.</strong> {issue.question}</span>
      <span style={small}>Awards nothing until fixed: {issue.issueText}{issue.keyAsGiven ? ` (the key said "${issue.keyAsGiven}")` : ''}. Options: {(issue.options || []).join(' · ')}</span>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        <input aria-label={`Answer for question ${issue.number}`} style={{ ...field, maxWidth: 220 }} value={answer} placeholder="The option, or its letter" onChange={(e) => { setAnswer(e.target.value); flow.cancel(); }} />
        <input aria-label={`Reason for question ${issue.number}`} style={{ ...field, flex: 1, minWidth: 200 }} value={reason} placeholder={`Reason (at least ${REASON_MIN} characters)`} onChange={(e) => { setReason(e.target.value); flow.cancel(); }} />
        {!flow.shown && <Button size="sm" variant="secondary" disabled={flow.busy || !ready} onClick={() => flow.preview(body)}>Check</Button>}
      </div>
      {flow.shown && (
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          <span>Key becomes "{flow.shown.after}". {flow.shown.toRemark ? `${flow.shown.toRemark} re-marked.` : 'Nobody to re-mark yet.'}</span>
          <Button size="sm" disabled={flow.busy} onClick={() => flow.confirm(body)}>Fix the key</Button>
          <Button size="sm" variant="ghost" disabled={flow.busy} onClick={flow.cancel}>Cancel</Button>
        </div>
      )}
      {flow.err && <span role="alert" style={{ ...small, color: 'var(--status-danger-text)' }}>{flow.err}</span>}
    </li>
  );
}

/* The stage's answer keys. Shown only for a paper that needs a confirmed
   key, has questions to fix, or when the stage has several papers. */
export default function AnswerKeyPanel({ papers, choiceNeeded, onChanged }) {
  const shown = (papers || []).filter((p) => p.key.required || p.keyIssues.length || choiceNeeded);
  if (!shown.length) return null;
  return (
    <Card>
      <CardHeader title="Answer keys" subtitle="Nobody is marked on a school paper until a named person confirms its key against the paper's own Google Form." />
      <div style={{ display: 'grid', gap: 18 }}>
        {shown.map((p) => (
          <section key={p.materialId} aria-label={`Paper ${p.paperName || p.title}`} style={{ display: 'grid', gap: 8 }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <strong>{p.paperName || p.title || 'Paper'}</strong>
              <span style={small}>{p.questions} questions · {p.totalMarks} marks</span>
              {p.key.confirmed ? (
                <StatusPill tone="success">Key confirmed</StatusPill>
              ) : p.key.required ? (
                <StatusPill tone="warning">Key not confirmed</StatusPill>
              ) : null}
              {p.held > 0 && <StatusPill tone="warning" dot={false}>{p.held} held</StatusPill>}
              {p.awaitingWrittenMark > 0 && <StatusPill tone="info" dot={false}>{p.awaitingWrittenMark} written to mark</StatusPill>}
            </div>
            {p.key.confirmed && (
              <span style={small}>
                Confirmed by {p.key.confirmedBy} on {new Date(p.key.confirmedAt).toLocaleDateString()} against{' '}
                <a href={p.key.formUrl} target="_blank" rel="noreferrer">the paper's Google Form</a>.
              </span>
            )}
            {p.key.numberingNote && <span style={small}>{p.key.numberingNote}</span>}
            {!p.shape.matches && (
              <details>
                <summary style={small}>How this paper differs from the group's paper</summary>
                <ul style={{ ...small, margin: '4px 0 0 18px' }}>{p.shape.notes.map((n) => <li key={n}>{n}</li>)}</ul>
              </details>
            )}
            {p.keyIssues.length > 0 && (
              <ul aria-label="Questions to fix" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                {p.keyIssues.map((k) => <FixKey key={k.questionId} paper={p} issue={k} onChanged={onChanged} />)}
              </ul>
            )}
            {p.key.required && !p.key.confirmed && <ConfirmKey paper={p} onChanged={onChanged} />}
          </section>
        ))}
      </div>
    </Card>
  );
}

/* Which paper a candidate sits, before the exam (a stage with several). */
export function PaperChoice({ resumeId, stageId, papers, current, onChanged }) {
  const [name, setName] = useState(current || '');
  const flow = usePreviewThenConfirm(`/written-test/${resumeId}/stages/${stageId}/paper`, onChanged);
  const names = (papers || []).map((p) => p.paperName).filter(Boolean);
  return (
    <div style={{ display: 'grid', gap: 4, justifyItems: 'start', whiteSpace: 'normal' }}>
      <span style={small}>{current ? `Sits: ${current}` : 'No paper chosen yet — the exam link will not open'}</span>
      <div style={{ display: 'flex', gap: 6 }}>
        <select aria-label="Paper" value={name} onChange={(e) => { setName(e.target.value); flow.cancel(); }} style={{ ...field, width: 'auto' }}>
          <option value="">Choose a paper…</option>
          {names.map((n) => <option key={n} value={n}>{n}</option>)}
        </select>
        {!flow.shown && <Button size="sm" variant="secondary" disabled={flow.busy || !name || name === current} onClick={() => flow.preview({ paperName: name })}>Check</Button>}
      </div>
      {flow.shown && (
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <span style={small}>{flow.shown.candidateName} sits {flow.shown.paperName}.</span>
          <Button size="sm" disabled={flow.busy} onClick={() => flow.confirm({ paperName: name })}>Confirm</Button>
        </div>
      )}
      {flow.err && <span role="alert" style={{ ...small, color: 'var(--status-danger-text)' }}>{flow.err}</span>}
    </div>
  );
}

/* A person's mark for a school paper's written answer (never the AI's). */
export function WrittenMarkForm({ resumeId, stageId, stageResult, onChanged }) {
  const pending = (stageResult?.examDetails?.gradedAnswers || []).filter((g) => g.pendingGrade);
  const [marks, setMarks] = useState({});
  const [remark, setRemark] = useState('');
  const [open, setOpen] = useState(false);
  const flow = usePreviewThenConfirm(`/written-test/${resumeId}/stages/${stageId}/written-mark`, onChanged);
  if (!pending.length) return null;
  const body = { marks: pending.map((g) => ({ questionId: String(g.questionId), points: Number(marks[g.questionId]) })), reason: remark.trim() };
  const ready = pending.every((g) => marks[g.questionId] !== undefined && marks[g.questionId] !== '');
  if (!open) return <button type="button" onClick={() => setOpen(true)} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit', fontSize: 'var(--fs-caption)', color: 'var(--brand-primary-ink)' }}>Mark the written answer</button>;
  return (
    <div style={{ display: 'grid', gap: 6, minWidth: 260, whiteSpace: 'normal' }}>
      {pending.map((g, i) => (
        <div key={String(g.questionId)} style={{ display: 'grid', gap: 4 }}>
          <span style={small}>Answer {i + 1}: {g.writtenAnswer || '(no answer)'}</span>
          <label style={{ ...small, display: 'flex', gap: 6, alignItems: 'center' }}>
            Mark
            <input aria-label={`Mark for answer ${i + 1}`} type="number" min={0} max={g.points} step={0.5} value={marks[g.questionId] ?? ''}
              onChange={(e) => { setMarks({ ...marks, [g.questionId]: e.target.value }); flow.cancel(); }} style={{ ...field, width: 80 }} />
            out of {g.points}
          </label>
        </div>
      ))}
      <input aria-label="Remark" style={field} value={remark} placeholder="Remark (optional)" onChange={(e) => setRemark(e.target.value)} />
      {!flow.shown ? (
        <div style={{ display: 'flex', gap: 6 }}>
          <Button size="sm" disabled={flow.busy || !ready} onClick={() => flow.preview(body)}>Check</Button>
          <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
        </div>
      ) : (
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={small}>Score becomes {flow.shown.score}% (pass mark {flow.shown.passingScore}%).</span>
          <Button size="sm" disabled={flow.busy} onClick={() => flow.confirm(body)}>Save the mark</Button>
          <Button size="sm" variant="ghost" disabled={flow.busy} onClick={flow.cancel}>Cancel</Button>
        </div>
      )}
      {flow.err && <span role="alert" style={{ ...small, color: 'var(--status-danger-text)' }}>{flow.err}</span>}
    </div>
  );
}
